// PreviewTester/utils/transformationRules.js

import {
  extractImageUrls,
  isImageUrl,
  escapeRegExp,
  getNthChildPath,
  getRelativePath,
} from "./renderingUtils";

import {
  cleanSelectors,
  createButtonStep,
  createButtonGroupStep,
  createStyleStep,
  createStructuralStep,
  createMediaStep,
  createCombinedStep,
  isButtonStep,
  isButtonGroupStep,
  isStyleStep,
  isStructuralStep,
  isMediaStep,
  isCombinedStep,
} from "./transformationSchema";


/**
 * Transformation Rules Engine
 * Handles: bold, italic, underline, table, card, json
 * Uses node-based selectors (nth-child) for reliable targeting with any data
 *
 * TRANSFORMATION TYPES:
 * 1. STYLE TRANSFORMATIONS (Apply CSS classes): highlight, bold, italic, underline, strikethrough
 *    - Work on ANY selection (single or multiple fields)
 *    - Do NOT change HTML structure
 *
 * 2. STRUCTURAL TRANSFORMATIONS (Change HTML structure): table, cards, json, list
 *    - Work on STRUCTURAL multi-node selections (entire items with multiple fields)
 *    - Change the HTML structure (e.g., convert to table)
 */

// ============================================================
// STYLE TRANSFORMATIONS - Apply CSS classes, no structure change
// ============================================================
export const STYLE_TRANSFORMATIONS = {
  HIGHLIGHT: "highlight",
  BOLD: "bold",
  ITALIC: "italic",
  UNDERLINE: "underline",
  STRIKETHROUGH: "strikethrough",
  IS_BUTTON: "isButton",
};

// Add to STYLE_TRANSFORMATION_LABELS
export const STYLE_TRANSFORMATION_LABELS = {
  [STYLE_TRANSFORMATIONS.HIGHLIGHT]: "✨ Highlight",
  [STYLE_TRANSFORMATIONS.BOLD]: "𝐁 Bold",
  [STYLE_TRANSFORMATIONS.ITALIC]: "𝐼 Italic",
  [STYLE_TRANSFORMATIONS.UNDERLINE]: "U̲ Underline",
  [STYLE_TRANSFORMATIONS.STRIKETHROUGH]: "S̶t̶r̶i̶k̶e̶",
  [STYLE_TRANSFORMATIONS.IS_BUTTON]: "🔘 Button",
};

// ============================================================
// STRUCTURAL TRANSFORMATIONS - Change HTML structure
// ============================================================
export const STRUCTURAL_TRANSFORMATIONS = {
  TABLE: "table",
  CARDS: "cards",
  JSON: "json",
  LIST: "list",
};

// User-friendly names for Structural Transformations
export const STRUCTURAL_TRANSFORMATION_LABELS = {
  [STRUCTURAL_TRANSFORMATIONS.TABLE]: "📊 Table View",
  [STRUCTURAL_TRANSFORMATIONS.CARDS]: "🃏 Card View",
  [STRUCTURAL_TRANSFORMATIONS.JSON]: "📋 JSON View",
  [STRUCTURAL_TRANSFORMATIONS.LIST]: "📝 List View",
};

// ============================================================
// LEGACY - Keeping for backward compatibility
// ============================================================
export const SINGLE_NODE_OPERATIONS = STYLE_TRANSFORMATIONS;
export const MULTI_NODE_OPERATIONS = STRUCTURAL_TRANSFORMATIONS;

// Add to CLASS_MAP
export const CLASS_MAP = {
  [STYLE_TRANSFORMATIONS.HIGHLIGHT]: "highlight",
  [STYLE_TRANSFORMATIONS.BOLD]: "bold",
  [STYLE_TRANSFORMATIONS.ITALIC]: "italic",
  [STYLE_TRANSFORMATIONS.UNDERLINE]: "underline",
  [STYLE_TRANSFORMATIONS.STRIKETHROUGH]: "strikethrough",
  [STYLE_TRANSFORMATIONS.IS_BUTTON]: "button",
};

// Selection types
export const SELECTION_TYPES = {
  EMPTY: "empty",
  SINGLE: "single",
  STYLE_MULTIPLE: "style_multiple",
  STRUCTURAL_MULTIPLE: "structural_multiple",
};

// Data attribute for tracking field nodes
export const FIELD_DATA_ATTR = "data-field";

// ============================================================
// PHASE WIZARD HELPERS
// ============================================================



/**
 * Get total operations across all phases
 */
export const getTotalOperations = (combinedStep) => {
  if (!combinedStep) return 0;
  const phases = combinedStep.phases;
  return (
    (phases.structural?.operations?.length || 0) +
    (phases.media?.operations?.length || 0) +
    (phases.styles?.operations?.length || 0)
  );
};

/**
 * Add operation to current phase
 */
export const addOperationToPhase = (combinedStep, phaseName, operationData) => {
  if (!combinedStep) return null;

  const newStep = {
    ...combinedStep,
    phases: {
      ...combinedStep.phases,
      [phaseName]: {
        operations: [
          ...(combinedStep.phases[phaseName]?.operations || []),
          operationData,
        ],
      },
    },
  };

  return newStep;
};

/**
 * Remove operation from a phase
 */
export const removeOperationFromPhase = (
  combinedStep,
  phaseName,
  operationIndex,
) => {
  if (!combinedStep) return null;

  const phase = combinedStep.phases[phaseName];
  if (!phase || !phase.operations) return combinedStep;

  const newOperations = phase.operations.filter(
    (_, index) => index !== operationIndex,
  );

  return {
    ...combinedStep,
    phases: {
      ...combinedStep.phases,
      [phaseName]: {
        operations: newOperations,
      },
    },
  };
};

/**
 * Finalize the combined step (mark as validated)
 */
export const finalizeCombinedStep = (combinedStep) => {
  if (!combinedStep) return null;
  return {
    ...combinedStep,
    validated: true,
  };
};

/**
 * Move to next phase
 */
export const nextPhase = (combinedStep) => {
  if (!combinedStep) return null;
  const nextPhase = Math.min((combinedStep.currentPhase || 0) + 1, 2);
  return {
    ...combinedStep,
    currentPhase: nextPhase,
  };
};

/**
 * Move to previous phase
 */
export const prevPhase = (combinedStep) => {
  if (!combinedStep) return null;
  const prevPhase = Math.max((combinedStep.currentPhase || 0) - 1, 0);
  return {
    ...combinedStep,
    currentPhase: prevPhase,
  };
};

export const getLoopContext = (element, container, isFullRow = false) => {
  console.log("[getLoopContext] START");
  console.log("[getLoopContext] 📥 Full Input:", {
    element: element,
    elementTag: element?.tagName,
    elementClass: element?.className,
    elementText: element?.textContent?.trim()?.substring(0, 100),
    container: container,
    containerTag: container?.tagName,
    containerClass: container?.className,
    isFullRow: isFullRow,
  });

  if (!element || !container) {
    console.log("[getLoopContext] ⚠️ Missing element or container");
    return null;
  }

  // If this is a full row selection, we need to find the row element
  let targetElement = element;
  if (isFullRow) {
    // Find the closest row
    const rowElement = element.closest?.(".row");
    if (rowElement) {
      targetElement = rowElement;
      console.log(
        "[getLoopContext] 🔄 Full row selection, using row element instead of child",
      );
    }
  }

  let current = targetElement;
  let loopElement = null;

  // Walk up the DOM tree to find the nearest for-loop-started element (tag agnostic)
  while (current && current !== container && current !== document.body) {
    if (current.classList && current.classList.contains("for-loop-started")) {
      loopElement = current;
      break;
    }
    current = current.parentElement;
  }

  if (!loopElement) {
    console.log("[getLoopContext] ⚠️ No for-loop-started element found");
    return null;
  }

  // Generate the absolute path to the loop element
  const loopSelector = getNthChildPath(loopElement, container);

  // Generate the relative path from the loop element to the target element
  const relativePath = getRelativePath(targetElement, loopElement);

  console.log("[getLoopContext] ✅ Found loop context:", {
    loopSelector,
    relativePath,
    loopVar: loopElement.getAttribute("data-loop-var"),
    isFullRow: isFullRow,
    targetElement: targetElement.tagName + "." + targetElement.className,
  });

  return {
    loopElement,
    loopSelector,
    relativePath,
    loopVar: loopElement.getAttribute("data-loop-var") || "unknown",
  };
};

// ============================================================
// applyMediaTransformation - Updated to use minimal schema
// ============================================================
export const applyMediaTransformation = (
  selectedText,
  operation,
  options = {},
) => {
  console.log(
    "[Transformation] 🖼️ applyMediaTransformation:",
    operation,
    options,
  );

  // ✅ Get image dimensions from options
  const {
    width = 250,
    height = 250,
    fullContent = "",
    preserveAspectRatio = true,
    operations = null,  // Support for multiple operations
    style_meta = null,  // Support for style metadata
    selectors = null,   // Support for selectors
    fields = null,      // Support for fields
  } = options;

  const contentToProcess = fullContent || selectedText;

  // Check if images are already rendered
  if (
    contentToProcess.includes('class="preview-image rendered-image"') ||
    contentToProcess.includes('class="rendered-image"')
  ) {
    console.log("[Transformation] 🖼️ Images already rendered, skipping");
    return {
      success: true,
      result: contentToProcess,
      data: {
        imageUrls: [],
        count: 0,
        alreadyRendered: true,
        width,
        height,
        preserveAspectRatio,
      },
    };
  }

  // ============================================================
  // STEP 1: Extract image URLs from the content
  // ============================================================
  const imageUrls = extractImageUrls(contentToProcess);

  if (imageUrls.length === 0) {
    return {
      success: false,
      error: "No image URLs found in the content",
      data: { imageUrls: [], count: 0 },
    };
  }

  console.log(`[Transformation] 🖼️ Found ${imageUrls.length} image URLs`);

  // ============================================================
  // STEP 2: Parse the content into DOM and replace images
  // ============================================================
  const parser = new DOMParser();
  const doc = parser.parseFromString(contentToProcess, "text/html");

  let totalReplacedCount = 0;
  let replacedUrls = [];

  // Find all elements that might contain image URLs (tag agnostic)
  const allElements = doc.querySelectorAll("div, p, td, th, li, span, a, img");

  for (const element of allElements) {
    // Check if this is already an image element
    if (element.tagName.toLowerCase() === 'img') {
      const src = element.getAttribute('src');
      if (src && isImageUrl(src)) {
        // Already an image, maybe update dimensions
        if (width && height) {
          element.setAttribute('width', width);
          element.setAttribute('height', height);
          element.style.width = `${width}px`;
          element.style.height = `${height}px`;
          element.style.objectFit = preserveAspectRatio ? 'cover' : 'fill';
          totalReplacedCount++;
          replacedUrls.push(src);
          console.log(
            `[Transformation] 🖼️ Updated existing image dimensions: ${src.substring(0, 50)}... (${width}×${height})`,
          );
        }
      }
      continue;
    }

    // Check if this element has data-image-url attribute
    const dataUrl = element.getAttribute("data-image-url");
    if (dataUrl && isImageUrl(dataUrl)) {
      const matchingUrl = imageUrls.find((u) => u.url === dataUrl);
      if (matchingUrl) {
        const imgHtml = createImageHtml(dataUrl, width, height, preserveAspectRatio);
        const tempDiv = doc.createElement("div");
        tempDiv.innerHTML = imgHtml;
        const newElement = tempDiv.firstChild;
        if (newElement) {
          element.parentNode.replaceChild(newElement, element);
          totalReplacedCount++;
          replacedUrls.push(dataUrl);
          console.log(
            `[Transformation] 🖼️ Replaced image via data-image-url: ${dataUrl.substring(0, 50)}... (${width}×${height})`,
          );
        }
      }
      continue;
    }

    // Check if this element has the image-url class
    if (element.classList && element.classList.contains("image-url")) {
      const textContent = element.textContent?.trim() || "";
      const matchingUrl = imageUrls.find(
        (u) => u.url === textContent || textContent.includes(u.url),
      );
      if (matchingUrl) {
        const imgHtml = createImageHtml(matchingUrl.url, width, height, preserveAspectRatio);
        const tempDiv = doc.createElement("div");
        tempDiv.innerHTML = imgHtml;
        const newElement = tempDiv.firstChild;
        if (newElement) {
          element.parentNode.replaceChild(newElement, element);
          totalReplacedCount++;
          replacedUrls.push(matchingUrl.url);
          console.log(
            `[Transformation] 🖼️ Replaced image via image-url class: ${matchingUrl.url.substring(0, 50)}... (${width}×${height})`,
          );
        }
      }
      continue;
    }

    // Check if the element's text content is an image URL
    const textContent = element.textContent?.trim() || "";
    if (textContent) {
      const matchingUrl = imageUrls.find(
        (u) => textContent.includes(u.url) || u.url.includes(textContent),
      );
      if (matchingUrl) {
        const isStandalone = textContent.trim() === matchingUrl.url.trim();
        if (isStandalone) {
          const imgHtml = createImageHtml(matchingUrl.url, width, height, preserveAspectRatio);
          const tempDiv = doc.createElement("div");
          tempDiv.innerHTML = imgHtml;
          const newElement = tempDiv.firstChild;
          if (newElement) {
            element.parentNode.replaceChild(newElement, element);
            totalReplacedCount++;
            replacedUrls.push(matchingUrl.url);
            console.log(
              `[Transformation] 🖼️ Replaced standalone URL in element: ${matchingUrl.url.substring(0, 50)}... (${width}×${height})`,
            );
          }
        }
      }
    }
  }

  // ============================================================
  // STEP 3: Serialize back to string
  // ============================================================
  const body = doc.body;
  let serialized = "";
  for (const child of body.children) {
    serialized += child.outerHTML;
  }

  console.log(`[Transformation] 🖼️ Replaced ${totalReplacedCount} images`);

  // ============================================================
  // STEP 4: Build the result with metadata
  // ============================================================
  const result = {
    success: true,
    result: serialized,
    data: {
      imageUrls: replacedUrls,
      count: imageUrls.length,
      replacedCount: totalReplacedCount,
      width,
      height,
      preserveAspectRatio,
    },
  };

  // If operations or style_meta were provided, include them for step creation
  if (operations || style_meta) {
    result.metadata = {
      operations: operations || [operation || 'images'],
      style_meta: style_meta || { full_row: false, alignment: 'left' },
      selectors: selectors || null,
      fields: fields || null,
    };
  }

  return result;
};

// Helper function to create image HTML with custom dimensions
const createImageHtml = (url, width, height, preserveAspectRatio = true) => {
  const cleanUrl = url.replace(/"/g, "&quot;");
  const objectFit = preserveAspectRatio ? 'cover' : 'fill';

  return `<div class="media-wrapper" style="display:inline-block;margin:4px 0;">
    <img 
      src="${cleanUrl}" 
      alt="Image" 
      class="preview-image rendered-image" 
      style="width:${width}px;height:${height}px;object-fit:${objectFit};border-radius:8px;border:1px solid #e2e8f0;max-width:100%;box-shadow:0 2px 4px rgba(0,0,0,0.1);" 
      loading="lazy" 
      onerror="this.style.display='none';this.parentElement.innerHTML='<div style=\\'color:#a0aec0;font-size:12px;\\'>🖼️ Image unavailable</div>';"
    />
  </div>`;
};

// ============================================================
// generatePositionSelector - Updated to return minimal selector format
// ============================================================
// PreviewTester/utils/transformationRules.js - Updated generatePositionSelector

export const generatePositionSelector = (
  selectedText,
  containerElement,
  range,
  from_where = 0,
) => {
  console.log(
    "from_wheredddd",
    from_where,
    selectedText,
    containerElement,
    range,
  );

  if (!selectedText || !selectedText.trim()) {
    console.log(
      "[generatePositionSelector] ⏭️ No text selected, returning empty",
    );
    return { type: "text_pattern", start: "", end: "", _empty: true };
  }

  if (!containerElement) {
    console.error(
      "[generatePositionSelector] ❌ containerElement is null or undefined",
    );
    throw new Error("containerElement is required and must be item-ui-screen");
  }

  if (
    !containerElement.classList ||
    !containerElement.classList.contains("item-ui-screen")
  ) {
    console.error(
      "[generatePositionSelector] ❌ containerElement is not item-ui-screen",
      {
        tagName: containerElement.tagName,
        className: containerElement.className,
        classList: containerElement.classList
          ? Array.from(containerElement.classList)
          : [],
      },
    );
    throw new Error(
      `containerElement must be item-ui-screen, got: ${containerElement.className || containerElement.tagName}`,
    );
  }

  const screenContainer = containerElement;
  console.log("[generatePositionSelector] ✅ Valid container (item-ui-screen)");

  if (!range) {
    console.warn("[generatePositionSelector] ⚠️ Missing range");
    return { type: "text_pattern", start: "", end: "" };
  }

  try {
    let startElement = range.startContainer;
    let endElement = range.endContainer;

    if (startElement.nodeType === 3) startElement = startElement.parentElement;
    if (endElement.nodeType === 3) endElement = endElement.parentElement;

    // ============================================================
    // NEW: Detect if we're inside a structural transformation
    // ============================================================
    const isInsideStructural = (el) => {
      let current = el;
      while (current && current !== screenContainer) {
        if (current.classList) {
          if (
            current.classList.contains("previewCard") ||
            current.classList.contains("previewCardContainer") ||
            current.classList.contains("previewTable") ||
            current.classList.contains("previewList") ||
            current.classList.contains("previewListItem")
          ) {
            return true;
          }
        }
        current = current.parentElement;
      }
      return false;
    };

    const structuralContext =
      isInsideStructural(startElement) || isInsideStructural(endElement);

    // ============================================================
    // NEW: Find the structural container (card, table, etc.)
    // ============================================================
    const findStructuralContainer = (el) => {
      let current = el;
      while (current && current !== screenContainer) {
        if (current.classList) {
          if (
            current.classList.contains("previewCard") ||
            current.classList.contains("previewTable") ||
            current.classList.contains("previewList")
          ) {
            return current;
          }
        }
        current = current.parentElement;
      }
      return null;
    };

    // ============================================================
    // If inside structural transformation, find the loop container
    // ============================================================
    // ============================================================
    // Inside a structural transformation (card / table / list).
    //
    // Two sub-cases:
    //   A. The structural container has .row children → it wraps
    //      the row layout (this is the current card model). Keep the
    //      row-based path computation, but return the loop-anchored
    //      result with both start AND end resolved.
    //   B. The structural container replaces the row layout (table,
    //      list). Fall back to the legacy behavior of anchoring to a
    //      single element.
    // ============================================================
    if (structuralContext) {
      console.log("[generatePositionSelector] 🔄 Inside structural transformation");

      let loopContainer = null;
      let current = startElement;
      while (current && current !== screenContainer) {
        if (current.classList && current.classList.contains("for-loop-started")) {
          loopContainer = current;
          break;
        }
        current = current.parentElement;
      }

      if (loopContainer) {
        const loopSelector = getNthChildPath(loopContainer, screenContainer);
        const structuralEl =
          findStructuralContainer(startElement) ||
          findStructuralContainer(endElement);

        if (structuralEl) {
          // ------------------------------------------------------------
          // Sub-case A: structural container wraps .row children.
          // Card mode. Compute start AND end paths.
          // ------------------------------------------------------------
          const hasRowChildren = structuralEl.querySelector(".row") !== null;

          if (hasRowChildren) {
            console.log("[generatePositionSelector] 📦 Structural container has .row children — using row-based paths");

            const getRow = (el) => el?.closest?.(".row") || null;
            const startRowEl = getRow(startElement);
            const endRowEl = getRow(endElement);

            // ------------------------------------------------------------
            // ✅ Preserve the exact target when the selection is inside a
            //    single value (or a partial span within a row). Collapse
            //    to the row only when the selection actually covers the
            //    entire row.
            //
            //    For example, selecting "john@example.com" inside a card:
            //      • startElement = the .value child
            //      • endElement   = the same .value child
            //    isFullRowCovered() returns false → keep the value path
            //    (e.g. "div:nth-child(2) > div:nth-child(3)").
            //
            //    Selecting the whole row (key+sep+value via drag) OR the
            //    row element itself:
            //    isFullRowCovered() returns true → collapse to row path
            //    (e.g. "div:nth-child(2)").
            // ------------------------------------------------------------
            const isFullRowCovered = (row, startEl, endEl) => {
              if (!row || !startEl || !endEl) return false;

              // If both endpoints are the row element itself, full row.
              if (startEl === row && endEl === row) return true;

              const rowChildren = Array.from(row.children);
              if (rowChildren.length === 0) return false;

              // If startEl or endEl is a text node, walk up to element.
              const toEl = (n) => (n?.nodeType === 3 ? n.parentElement : n);
              const sEl = toEl(startEl);
              const eEl = toEl(endEl);

              // If both endpoints resolve to the same non-row child,
              // it's a value-level selection, not a full row.
              if (sEl && eEl && sEl === eEl && sEl !== row) {
                return false;
              }

              // Find the positions of the endpoints within the row.
              // Direct-child check: the endpoint is a direct child of the
              // row (key / separator / value), and it's the first / last.
              const startIdx = rowChildren.indexOf(sEl);
              const endIdx = rowChildren.indexOf(eEl);

              if (startIdx !== -1 && endIdx !== -1) {
                // Both endpoints are direct children of the row.
                return startIdx === 0 && endIdx === rowChildren.length - 1;
              }

              // If endpoints are nested deeper (e.g. text inside .value),
              // try to find the nearest direct-child ancestor within the row.
              const nearestChild = (el) => {
                let curr = el;
                while (curr && curr.parentElement !== row) {
                  curr = curr.parentElement;
                }
                return curr && curr.parentElement === row ? curr : null;
              };

              const sChild = nearestChild(sEl);
              const eChild = nearestChild(eEl);

              if (!sChild || !eChild) return false;

              const sIdx = rowChildren.indexOf(sChild);
              const eIdx = rowChildren.indexOf(eChild);

              if (sIdx === -1 || eIdx === -1) return false;

              return sIdx === 0 && eIdx === rowChildren.length - 1;
            };

            let startTarget = startElement;
            let endTarget = endElement;

            if (
              startRowEl &&
              isFullRowCovered(startRowEl, startElement, endElement)
            ) {
              startTarget = startRowEl;
            }
            if (
              endRowEl &&
              isFullRowCovered(endRowEl, startElement, endElement)
            ) {
              endTarget = endRowEl;
            }

            const startRelativePath = getRelativePath(startTarget, loopContainer);
            const endRelativePath = getRelativePath(endTarget, loopContainer);

            console.log("[generatePositionSelector] 📦 Card-wrapped row paths:", {
              startRelativePath,
              endRelativePath,
              startTargetTag: startTarget?.tagName,
              startTargetClass: startTarget?.className,
            });

            const isFullRow =
              startTarget === startRowEl &&
              endTarget === endRowEl &&
              startRowEl === endRowEl;

            return {
              loopSelector,
              start: startRelativePath,
              end: endRelativePath,
              _metadata: {
                isInsideStructural: true,
                structuralType: "card",
                isInsideLoop: true,
                loopVar: loopContainer.getAttribute("data-loop-var") || "unknown",
                relativePath: startRelativePath,
                endRelativePath: endRelativePath,
                isMultiElement: startRelativePath !== endRelativePath,
                isFullRow,
                startRowIndex:
                  startRowEl
                    ? Array.from(loopContainer.querySelectorAll(".row")).indexOf(startRowEl)
                    : -1,
                endRowIndex:
                  endRowEl
                    ? Array.from(loopContainer.querySelectorAll(".row")).indexOf(endRowEl)
                    : -1,
                isMultiRowSelection: startRowEl !== endRowEl,
              },
            };
          }

          // ------------------------------------------------------------
          // Sub-case B: legacy structural container without .row children
          // (table / list). Keep the original single-anchor behaviour.
          // ------------------------------------------------------------
          console.log("[generatePositionSelector] 📊 Legacy structural container (no rows) — single anchor");

          let targetElement = startElement;
          if (
            startElement.tagName === "H4" ||
            startElement.tagName === "P" ||
            startElement.tagName === "STRONG"
          ) {
            targetElement = startElement;
          } else if (
            startElement.parentElement &&
            (startElement.parentElement.tagName === "H4" ||
              startElement.parentElement.tagName === "P")
          ) {
            targetElement = startElement.parentElement;
          }

          const relativePath = getRelativePath(targetElement, loopContainer);

          let startPath = relativePath;
          let endPath = relativePath;

          if (
            targetElement.classList &&
            targetElement.classList.contains("previewCardTitle")
          ) {
            const cardParent = targetElement.closest(".previewCard");
            if (cardParent) {
              const cardPath = getRelativePath(cardParent, loopContainer);
              startPath = cardPath;
              endPath = cardPath;
            }
          } else if (
            targetElement.tagName === "STRONG" ||
            targetElement.tagName === "P"
          ) {
            const parentCard = targetElement.closest(".previewCard");
            if (parentCard) {
              const cardPath = getRelativePath(parentCard, loopContainer);
              startPath = cardPath;
              endPath = cardPath;
            }
          }

          let structuralType = "unknown";
          if (structuralEl.classList.contains("previewCard")) structuralType = "card";
          else if (structuralEl.classList.contains("previewTable")) structuralType = "table";
          else if (structuralEl.classList.contains("previewList")) structuralType = "list";

          return {
            loopSelector,
            start: startPath,
            end: endPath,
            _metadata: {
              isInsideStructural: true,
              structuralType,
              isInsideLoop: true,
              loopVar: loopContainer.getAttribute("data-loop-var") || "unknown",
              relativePath,
              targetTag: targetElement.tagName,
              targetClass: targetElement.className,
              isMultiRowSelection: false,
              startRowIndex: -1,
              endRowIndex: -1,
            },
          };
        }
      }

      // Container-level fallback (unchanged)
      const structuralEl =
        findStructuralContainer(startElement) ||
        findStructuralContainer(endElement);
      if (structuralEl) {
        let loopContainer = null;
        let current = structuralEl;
        while (current && current !== screenContainer) {
          if (current.classList && current.classList.contains("for-loop-started")) {
            loopContainer = current;
            break;
          }
          current = current.parentElement;
        }

        if (loopContainer) {
          const loopSelector = getNthChildPath(loopContainer, screenContainer);
          const structuralPath = getRelativePath(structuralEl, loopContainer);

          let structuralType = "unknown";
          if (structuralEl.classList.contains("previewCard")) structuralType = "card";
          else if (structuralEl.classList.contains("previewTable")) structuralType = "table";
          else if (structuralEl.classList.contains("previewList")) structuralType = "list";

          return {
            loopSelector,
            start: structuralPath,
            end: structuralPath,
            _metadata: {
              isInsideStructural: true,
              structuralType,
              isInsideLoop: true,
              loopVar: loopContainer.getAttribute("data-loop-var") || "unknown",
              isMultiRowSelection: false,
              startRowIndex: -1,
              endRowIndex: -1,
            },
          };
        }
      }
    }

    // ============================================================
    // EXISTING LOGIC: Find .row elements (class based, tag agnostic)
    // ============================================================
    let startRow = startElement.closest?.(".row");
    let endRow = endElement.closest?.(".row");

    const lines = selectedText.split("\n").filter((l) => l.trim().length > 0);
    const isMultiLineSelection = lines.length > 1;

    if (!startRow && startElement) {
      startRow =
        startElement.closest?.(".row") ||
        startElement.parentElement?.closest?.(".row");
    }

    if (!endRow && endElement) {
      endRow =
        endElement.closest?.(".row") ||
        endElement.parentElement?.closest?.(".row");
    }

    if (!startRow && startElement) {
      startRow = startElement.closest?.(".row");
    }
    if (!endRow && endElement) {
      endRow = endElement.closest?.(".row");
    }

    if (!startRow && endRow) {
      startRow = endRow;
      console.log(
        "[generatePositionSelector] 🔄 Using endRow as startRow (single row selection)",
      );
    } else if (startRow && !endRow) {
      endRow = startRow;
      console.log(
        "[generatePositionSelector] 🔄 Using startRow as endRow (single row selection)",
      );
    }

    if (!startRow && !endRow && startElement) {
      startRow = startElement.closest?.(".row");
      if (startRow) {
        endRow = startRow;
        console.log(
          "[generatePositionSelector] 🔄 Found row from startElement via closest",
        );
      }
    }

    if (!startRow && !endRow && endElement) {
      startRow = endElement.closest?.(".row");
      if (startRow) {
        endRow = startRow;
        console.log(
          "[generatePositionSelector] 🔄 Found row from endElement via closest",
        );
      }
    }

    if (!startRow || !endRow) {
      console.error(
        "[generatePositionSelector] ❌ Could not find rows for selection",
        {
          startRow: !!startRow,
          endRow: !!endRow,
          startElement: startElement,
          endElement: endElement,
          startElementTag: startElement?.tagName,
          endElementTag: endElement?.tagName,
        },
      );
      throw new Error(
        "Could not find rows for the selected text. Please select text within a row.",
      );
    }

    const isMultiRowSelection = startRow && endRow && startRow !== endRow;

    console.log("[generatePositionSelector] 📊 Row detection:", {
      hasStartRow: !!startRow,
      hasEndRow: !!endRow,
      isMultiRowSelection,
      isMultiLineSelection,
      startRowText: startRow?.textContent?.trim()?.substring(0, 30),
      endRowText: endRow?.textContent?.trim()?.substring(0, 30),
    });

    // Get row indices
    let startRowIndex = -1;
    let endRowIndex = -1;

    if (startRow && screenContainer) {
      const allRows = screenContainer.querySelectorAll(".row");
      startRowIndex = Array.from(allRows).indexOf(startRow);
      if (startRowIndex === -1) {
        console.error(
          "[generatePositionSelector] ❌ Could not find start row index",
          {
            startRow: startRow,
            allRowsCount: allRows.length,
          },
        );
        throw new Error("Could not find start row index in the DOM");
      }
    }

    if (endRow && screenContainer) {
      const allRows = screenContainer.querySelectorAll(".row");
      endRowIndex = Array.from(allRows).indexOf(endRow);
      if (endRowIndex === -1) {
        console.error(
          "[generatePositionSelector] ❌ Could not find end row index",
          {
            endRow: endRow,
            allRowsCount: allRows.length,
          },
        );
        throw new Error("Could not find end row index in the DOM");
      }
    }

    if (isMultiLineSelection && (startRowIndex === -1 || endRowIndex === -1)) {
      console.error(
        "[generatePositionSelector] ❌ Multi-line selection but row indices not found",
        {
          startRowIndex,
          endRowIndex,
          isMultiLineSelection,
        },
      );
      throw new Error(
        "Multi-line selection detected but row indices could not be determined",
      );
    }

    // ============================================================
    // DOM-BASED FULL ROW DETECTION - NO TEXT COMPARISON
    // ============================================================
    let isFullRowSelection = false;

    const getRowElement = (el) => {
      if (!el) return null;
      if (el.classList && el.classList.contains("row")) return el;
      return el.closest?.(".row") || null;
    };

    const startRowElement = getRowElement(startElement);
    const endRowElement = getRowElement(endElement);

    const isRowFullyCovered = (row, startEl, endEl) => {
      if (!row) return false;

      const rowChildren = Array.from(row.children);

      if (rowChildren.length === 0) {
        return startEl === row && endEl === row;
      }

      const startIndex = rowChildren.indexOf(startEl);
      const endIndex = rowChildren.indexOf(endEl);

      if (startIndex === -1 && endIndex === -1) {
        if (startEl === row && endEl === row) {
          return true;
        }
        return false;
      }

      if (startIndex === -1 || endIndex === -1) {
        return false;
      }

      return startIndex === 0 && endIndex === rowChildren.length - 1;
    };

    // Case 1: Single row selection
    if (startRowElement && endRowElement && startRowElement === endRowElement) {
      const row = startRowElement;
      isFullRowSelection = isRowFullyCovered(row, startElement, endElement);

      if (isFullRowSelection) {
        startElement = row;
        endElement = row;
        console.log(
          "[generatePositionSelector] ✅ Full row detected (DOM-based), using row for both start and end",
        );
      }
    }
    // Case 2: Multi-row selection
    else if (
      startRowElement &&
      endRowElement &&
      startRowElement !== endRowElement
    ) {
      const allRowsInRange = [];
      let currentRow = startRowElement;
      while (currentRow && currentRow !== endRowElement.nextElementSibling) {
        if (currentRow.classList && currentRow.classList.contains("row")) {
          allRowsInRange.push(currentRow);
        }
        currentRow = currentRow.nextElementSibling;
      }

      let allRowsFullyCovered = true;
      for (const row of allRowsInRange) {
        let startEl = row === startRowElement ? startElement : row;
        let endEl = row === endRowElement ? endElement : row;

        if (!isRowFullyCovered(row, startEl, endEl)) {
          allRowsFullyCovered = false;
          break;
        }
      }

      if (allRowsFullyCovered) {
        isFullRowSelection = true;
        startElement = startRowElement;
        endElement = endRowElement;
        console.log(
          "[generatePositionSelector] ✅ Full multi-row selection detected (DOM-based)",
        );
      }
    }
    // Case 3: Single element selection within a row
    else if (startRowElement) {
      const row = startRowElement;
      isFullRowSelection = isRowFullyCovered(row, startElement, endElement);

      if (isFullRowSelection) {
        startElement = row;
        endElement = row;
        console.log(
          "[generatePositionSelector] ✅ Full row detected (DOM-based, single element)",
        );
      }
    }

    console.log(
      "[generatePositionSelector] 📊 Full row detection (DOM-based):",
      {
        isFullRowSelection,
        isMultiRowSelection,
        isMultiLineSelection,
      },
    );

    if (isFullRowSelection && !isMultiRowSelection) {
      let rowContainer =
        startElement.closest?.(".row") || endElement.closest?.(".row");
      if (rowContainer) {
        startElement = rowContainer;
        endElement = rowContainer;
        console.log(
          "[generatePositionSelector] ✅ Full row collapsed:",
          rowContainer.textContent?.trim()?.substring(0, 50),
        );
      }
    }

    // Detect if we're inside a loop
    let isInsideForLoop = false;
    let loopVarName = null;
    let current = startElement;
    while (current && current !== screenContainer) {
      if (current.classList?.contains("for-loop-started")) {
        isInsideForLoop = true;
        loopVarName = current.getAttribute("data-loop-var");
        break;
      }
      current = current.parentElement;
    }

    // Get the paths
    let startPath = getNthChildPath(startElement, screenContainer);
    let endPath = getNthChildPath(endElement, screenContainer);
    if (!endPath) endPath = startPath;

    // ============================================================
    // Loop context handling - Return with metadata
    // ============================================================
    if (range && screenContainer) {
      try {
        let rangeStartElement = range.startContainer;
        let rangeEndElement = range.endContainer;

        if (rangeStartElement.nodeType === 3)
          rangeStartElement = rangeStartElement.parentElement;
        if (rangeEndElement.nodeType === 3)
          rangeEndElement = rangeEndElement.parentElement;

        let startInsideLoop = false;
        let checkStart = rangeStartElement;
        while (
          checkStart &&
          checkStart !== screenContainer &&
          checkStart !== document.body
        ) {
          if (
            checkStart.classList &&
            checkStart.classList.contains("for-loop-started")
          ) {
            startInsideLoop = true;
            break;
          }
          checkStart = checkStart.parentElement;
        }

        let endInsideLoop = false;
        let checkEnd = rangeEndElement;
        while (
          checkEnd &&
          checkEnd !== screenContainer &&
          checkEnd !== document.body
        ) {
          if (
            checkEnd.classList &&
            checkEnd.classList.contains("for-loop-started")
          ) {
            endInsideLoop = true;
            break;
          }
          checkEnd = checkEnd.parentElement;
        }

        if (startInsideLoop || endInsideLoop) {
          let elementForLoop = startInsideLoop
            ? rangeStartElement
            : rangeEndElement;

          const loopContext = getLoopContext(
            elementForLoop,
            screenContainer,
            isFullRowSelection,
          );
          if (loopContext) {
            const loopElement = loopContext.loopElement;

            let startRelativePath, endRelativePath;

            if (isFullRowSelection) {
              const rowElement = elementForLoop.closest?.(".row");
              if (rowElement) {
                startRelativePath = getRelativePath(rowElement, loopElement);
                endRelativePath = startRelativePath;
                console.log(
                  "[generatePositionSelector] 🔄 Full row: using row-level path:",
                  startRelativePath,
                );
              } else {
                startRelativePath = getRelativePath(
                  elementForLoop,
                  loopElement,
                );
                endRelativePath = startRelativePath;
              }
            } else if (
              startInsideLoop &&
              endInsideLoop &&
              rangeStartElement !== rangeEndElement
            ) {
              startRelativePath = getRelativePath(
                rangeStartElement,
                loopElement,
              );
              endRelativePath = getRelativePath(rangeEndElement, loopElement);
              console.log(
                "[generatePositionSelector] 🔄 Multi-element: using start/end paths:",
                { startRelativePath, endRelativePath },
              );
            } else {
              startRelativePath = getRelativePath(elementForLoop, loopElement);
              endRelativePath = startRelativePath;
            }

            // ✅ Return with _metadata for loop context
            return {
              loopSelector: loopContext.loopSelector,
              start: startRelativePath,
              end: endRelativePath,
              _metadata: {
                isInsideLoop: true,
                loopVar: loopContext.loopVar,
                loopSelector: loopContext.loopSelector,
                relativePath: startRelativePath,
                endRelativePath: endRelativePath,
                isMultiElement: startRelativePath !== endRelativePath,
                isFullRow: isFullRowSelection,
                // ✅ CRITICAL: Need row indices for multi-row selection inside loop
                startRowIndex: startRowIndex,
                endRowIndex: endRowIndex,
                isMultiRowSelection: isMultiRowSelection || isMultiLineSelection,
                isMultiLine: isMultiLineSelection,
                startRowText: startRow?.textContent?.trim()?.substring(0, 50),
                endRowText: endRow?.textContent?.trim()?.substring(0, 50),
                selectedTextPreview: selectedText?.substring(0, 100),
              }
            };
          }
        }
      } catch (err) {
        console.error(
          "[generatePositionSelector] ❌ Error checking loop context:",
          err,
        );
      }
    }

    // ✅ Return with _metadata for absolute selector
    console.log("[generatePositionSelector] END (absolute)", {
      startPath,
      endPath,
      isMultiRowSelection,
    });

    return {
      start: startPath,
      end: endPath,
      _metadata: {
        startRowIndex: startRowIndex,
        endRowIndex: endRowIndex,
        isMultiRowSelection: isMultiRowSelection || isMultiLineSelection,
        isMultiLine: isMultiLineSelection,
        isFullRow: isFullRowSelection,
        isInsideForLoop: isInsideForLoop,
        loopVarName: loopVarName,
        startRowText: startRow?.textContent?.trim()?.substring(0, 50),
        endRowText: endRow?.textContent?.trim()?.substring(0, 50),
        selectedTextPreview: selectedText?.substring(0, 100),
        screenIndex: screenContainer.getAttribute("data-item-ui-index"),
      }
    };
  } catch (e) {
    console.error("[generatePositionSelector] ❌ Error:", e.message);
    return {
      type: "text_pattern",
      start: "",
      end: "",
      _error: true,
      errorMessage: e.message,
    };
  }
};

// ============================================================
// DETECTION
// ============================================================

export const detectSelectionType = (
  selectedText,
  hasForLoopStarted = false,
) => {
  console.log("[Transformation] 🔍 detectSelectionType called");

  if (!selectedText || !selectedText.trim()) {
    return { type: SELECTION_TYPES.EMPTY, count: 0 };
  }

  const lines = selectedText
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) {
    return { type: SELECTION_TYPES.EMPTY, count: 0 };
  }

  const hasImageUrlClass =
    selectedText.includes('class="value image-url"') ||
    selectedText.includes("data-image-url=");

  // ============================================================
  // ✅ NEW: If selection is inside a for-loop-started container,
  // it's structurally transformable (regardless of content)
  // ============================================================
  if (hasForLoopStarted) {
    console.log(
      "[Transformation] ✅ Selection is inside a for-loop-started container → STRUCTURAL_MULTIPLE"
    );
    return {
      type: SELECTION_TYPES.STRUCTURAL_MULTIPLE,
      count: lines.length,
      fields: lines.map((line) => ({ full: line, key: "nested", value: line })),
      keys: [],
      isStructural: true,
      confidence: 100,
      hasImageUrlClass: hasImageUrlClass,
    };
  }

  // ============================================================
  // Fallback: old key-based detection for non-loop selections
  // ============================================================
  const keyPattern = /^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*/;
  const allKeys = [];
  const keyOccurrences = {};

  for (const line of lines) {
    const match = line.match(keyPattern);
    if (match) {
      const key = match[1];
      allKeys.push(key);
      keyOccurrences[key] = (keyOccurrences[key] || 0) + 1;
    }
  }

  const hasRepeatingKeys = Object.values(keyOccurrences).some((c) => c > 1);
  if (hasRepeatingKeys) {
    return {
      type: SELECTION_TYPES.STRUCTURAL_MULTIPLE,
      count: lines.length,
      fields: lines.map((line) => ({ full: line, key: "nested", value: line })),
      keys: Array.from(new Set(allKeys)),
      isStructural: true,
      confidence: 80,
      hasImageUrlClass: hasImageUrlClass,
    };
  }

  if (allKeys.length > 1) {
    return {
      type: SELECTION_TYPES.STYLE_MULTIPLE,
      count: allKeys.length,
      fields: lines.map((line) => ({ full: line, key: "nested", value: line })),
      keys: Array.from(new Set(allKeys)),
      isStructural: false,
      hasImageUrlClass: hasImageUrlClass,
    };
  }

  return {
    type: SELECTION_TYPES.SINGLE,
    count: 1,
    fields: lines.map((line) => ({ full: line, key: "nested", value: line })),
    keys: Array.from(new Set(allKeys)),
    hasImageUrlClass: hasImageUrlClass,
  };
};

// UPDATED: wrapWithElement - Now uses div and is tag agnostic
const wrapWithElement = (text, className) => {
  if (!text) return text;

  // Match any opening tag with class attribute
  const elementPattern = /<[^>]*class="([^"]*)"[^>]*>([\s\S]*?)<\/[^>]+>/;
  const match = text.match(elementPattern);

  if (match) {
    const existingClasses = match[1];
    const innerText = match[2];
    const classes = existingClasses.split(" ");

    if (classes.includes(className)) {
      const remainingClasses = classes.filter((c) => c !== className);
      if (remainingClasses.length > 0) {
        return `<div class="${remainingClasses.join(" ")}">${innerText}</div>`;
      } else {
        return innerText;
      }
    } else {
      const allClasses = [...new Set([...classes, className])].join(" ");
      return `<div class="${allClasses}">${innerText}</div>`;
    }
  }

  return `<div class="${className}">${text}</div>`;
};

// Keep the old name for backward compatibility, but use the new function
const wrapWithSpan = wrapWithElement;

// ============================================================
// PARSE FUNCTIONS
// ============================================================

export const parseSelectedText = (text) => {
  console.log("[Transformation] 🔍 parseSelectedText called");

  const lines = text
    .split("\n")
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length === 0) return [];

  const parsedItems = [];
  let currentItem = {};
  let itemStarterKey = null;
  let keyCounts = {};

  const allKeys = [];
  for (const line of lines) {
    if (/^[A-Za-z\s]+:$/.test(line)) continue;

    const match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.*)$/);
    if (match) {
      const key = match[1];
      allKeys.push(key);
      keyCounts[key] = (keyCounts[key] || 0) + 1;
    }
  }

  let maxCount = 0;
  for (const [key, count] of Object.entries(keyCounts)) {
    if (count > maxCount) {
      maxCount = count;
      itemStarterKey = key;
    }
  }

  for (const line of lines) {
    if (/^[A-Za-z\s]+:$/.test(line)) continue;

    const match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*(.*)$/);
    if (match) {
      const key = match[1];
      const value = match[2].trim();

      if (key === itemStarterKey && Object.keys(currentItem).length > 0) {
        parsedItems.push({ ...currentItem });
        currentItem = {};
      }

      currentItem[key] = value;
    }
  }

  if (Object.keys(currentItem).length > 0) {
    parsedItems.push({ ...currentItem });
  }

  console.log(`[Transformation] 📊 Parsed ${parsedItems.length} items`);
  return parsedItems;
};

// ============================================================
// HTML GENERATORS
// ============================================================

export const generateTableHTML = (items) => {
  console.log(
    "[MISSION CRITICAL generateTableHTML] items count:",
    items?.length || 0,
  );

  if (!items || items.length === 0) {
    return "<p>No data</p>";
  }

  const keys = Object.keys(items[0]);

  let html =
    '<table class="previewTable" border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse;width:100%;">';
  html += '<thead><tr style="background:#f7fafc;">';
  keys.forEach((key) => {
    html += `<th style="padding:8px;text-align:left;border:1px solid #e2e8f0;font-weight:600;">${key}</th>`;
  });
  html += "</tr></thead><tbody>";

  items.forEach((item, index) => {
    const bgColor = index % 2 === 0 ? "#ffffff" : "#f7fafc";
    html += `<tr style="background:${bgColor};">`;
    keys.forEach((key) => {
      const value = item[key] || "";
      html += `<td style="padding:8px;border:1px solid #e2e8f0;">${value}</td>`;
    });
    html += "</tr>";
  });

  html += "</tbody></table>";
  return html;
};

// Updated generateCardHTML - uses div for card elements
// PreviewTester/utils/renderingUtils.js

/**
 * Generate card HTML with proper image URL formatting for media transformations
 * UPDATED: Properly formats image URLs with value image-url class and data-image-url attribute
 */
export const generateCardHTML = (items, scrollMode = 'vertical') => {
  console.log("[generateCardHTML] here is scrollMode", scrollMode);

  const flexDirection = scrollMode === 'horizontal' ? 'row' : 'column';

  console.log("[generateCardHTML] items count:", items?.length || 0);
  console.log("[generateCardHTML] items:", items);
  console.log("[generateCardHTML] scrollMode:", scrollMode);

  if (!items || items.length === 0) return "<p>No data</p>";

  const containerStyle = `display:flex;flex-direction:${flexDirection};gap:16px;${scrollMode === 'horizontal' ? 'overflow-x:auto;flex-wrap:nowrap;' : ''}`;
  let html = `<div class="previewCardContainer" style="${containerStyle}">`;

  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    console.log(`[generateCardHTML] Generating card ${index + 1}:`, item);

    html += `<div class="previewCard" style="background:white;border-radius:8px;border:1px solid #e2e8f0;padding:16px;box-shadow:0 1px 3px rgba(0,0,0,0.1);">`;
    html += `<h4 class="previewCardTitle" style="margin:0 0 8px 0;color:#2d3748;">Item ${index + 1}</h4>`;

    Object.keys(item).forEach((key) => {
      const value = item[key] || "";
      const isImage = isImageUrl(value);

      let valueHtml;
      if (isImage) {
        // ✅ Properly format image URLs with class and data attribute
        valueHtml = `<span class="value image-url" data-image-url="${value}">${value}</span>`;
      } else {
        valueHtml = `<span class="value">${value}</span>`;
      }

      // Use div for card elements with proper structure
      html += `<p style="margin:4px 0;font-size:13px;display:flex;gap:4px;flex-wrap:wrap;">
        <span class="key">${key}:</span>
        ${valueHtml}
      </p>`;
    });

    html += "</div>";
  }

  html += "</div>";
  console.log("[generateCardHTML] Generated HTML length:", html.length);
  console.log("[generateCardHTML] Generated HTML:", html);

  return html;
};

export const generateListHTML = (items) => {
  if (!items || items.length === 0) return "<p>No data</p>";

  let html =
    '<ul class="previewList" style="list-style:none;padding:0;margin:0;">';

  items.forEach((item, index) => {
    html += `<li class="previewListItem" style="padding:8px 12px;background:${index % 2 === 0 ? "#ffffff" : "#f7fafc"};border-bottom:1px solid #e2e8f0;">`;
    html += `<strong>Item ${index + 1}:</strong> `;
    const parts = Object.entries(item).map(
      ([key, value]) => `${key}: ${value}`,
    );
    html += parts.join(" | ");
    html += "</li>";
  });

  html += "</ul>";
  return html;
};

// ============================================================
// TRANSFORMATION STEP CREATION - Updated to use minimal schema
// ============================================================

export const createMinimalTransformationStep = (
  selectedText,
  operation,
  result,
  data,
  type,
  template,
  sampleData,
  containerElement,
  range,
  options = {},
) => {
  console.log("[createMinimalTransformationStep] START");
  console.log("[createMinimalTransformationStep] type:", type);
  console.log("[createMinimalTransformationStep] operation:", operation);
  console.log(
    "[createMinimalTransformationStep] options has selectors:",
    !!options.selectors,
  );
  console.log(
    "[createMinimalTransformationStep] options has operations:",
    !!options.operations,
  );
  console.log(
    "[createMinimalTransformationStep] options has style_meta:",
    !!options.style_meta,
  );

  // ============================================================
  // STEP 1: Build selectors array from options
  // ============================================================
  let selectors = [];

  if (
    options.selectors &&
    Array.isArray(options.selectors) &&
    options.selectors.length > 0
  ) {
    selectors = options.selectors;
    console.log(
      "[createMinimalTransformationStep] ✅ Using provided selectors array, count:",
      selectors.length,
    );
  } else if (options.selector) {
    console.log(
      "[createMinimalTransformationStep] ✅ Converting legacy selector to array",
    );
    selectors = [options.selector];
  } else {
    console.log(
      "[createMinimalTransformationStep] ⚠️ No selectors provided, generating new one",
    );
    const singleSelector = generatePositionSelector(
      selectedText,
      containerElement,
      range,
      7,
    );
    if (singleSelector) {
      selectors = [singleSelector];
    }
  }

  if (selectors.length === 0) {
    console.warn(
      "[createMinimalTransformationStep] ⚠️ No selectors available, creating fallback",
    );
    selectors = [
      {
        start: "",
        end: "",
      },
    ];
  }

  // ============================================================
  // STEP 2: Clean selectors to minimal format
  // ============================================================
  const cleanedSelectors = cleanSelectors(selectors);
  console.log(
    "[createMinimalTransformationStep] 📊 Final selectors count:",
    cleanedSelectors.length,
  );

  // ============================================================
  // STEP 3: Extract fields from data
  // ============================================================
  let fields = [];
  const isStructural = type === "structural" || type === "multiple";

  if (isStructural && data && data.length > 0) {
    fields = Object.keys(data[0] || {});
  } else if (type === "style" || type === "single") {
    const selectionType = detectSelectionType(selectedText);
    if (selectionType) {
      if (
        selectionType.type === SELECTION_TYPES.STYLE_MULTIPLE &&
        selectionType.keys
      ) {
        fields = selectionType.keys;
      } else if (
        selectionType.type === SELECTION_TYPES.SINGLE &&
        selectionType.fields
      ) {
        const singleField = selectionType.fields[0];
        if (singleField && singleField.key) {
          fields = [singleField.key];
        }
      }
    }
  }

  if (fields.length === 0 && selectedText) {
    const lines = selectedText.split("\n").filter((l) => l.trim());
    const fieldKeys = new Set();
    for (const line of lines) {
      const match = line.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\s*[-:=>]\s*/);
      if (match) {
        fieldKeys.add(match[1]);
      }
    }
    fields = Array.from(fieldKeys);
  }

  // ============================================================
  // STEP 4: Create appropriate step based on type
  // ============================================================
  let step;

  if (type === "button" || operation === "isButton") {
    const buttonText = selectedText || fields[0] || "Button";
    const selectorPath = cleanedSelectors[0]?.start || "";

    step = createButtonStep(
      buttonText,
      selectorPath,
      `btn_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );

    if (cleanedSelectors.length > 1) {
      step.selectors = cleanedSelectors;
    }
  } else if (type === "button-group" || operation === "isButtonGroup") {
    const buttons =
      data?.map((item) => {
        const values = Object.values(item);
        return values.length > 0 ? String(values[0]) : "Button";
      }) || ["Button 1", "Button 2"];

    const groupName = selectedText || "Button Group";
    const selectorPath = cleanedSelectors[0]?.start || "";

    step = createButtonGroupStep(
      groupName,
      buttons,
      selectorPath,
      `btngrp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );

    if (cleanedSelectors.length > 1) {
      step.selectors = cleanedSelectors;
    }
  } else if (type === "media") {
    const imageSize = options.imageSize || { width: 250, height: 250 };

    step = createMediaStep(
      fields,
      cleanedSelectors,
      imageSize,
      `media_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );
  } else if (isStructural) {
    step = createStructuralStep(
      operation,
      fields,
      cleanedSelectors,
      `struct_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );
  } else if (type === "combined" && options.phases) {
    step = createCombinedStep(
      options.phases,
      cleanedSelectors,
      `combined_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );
  } else if (type === "style" || type === "single" || type === "styles") {
    let operations = [];
    if (
      options.operations &&
      Array.isArray(options.operations) &&
      options.operations.length > 0
    ) {
      operations = options.operations;
    } else if (operation) {
      operations = [operation];
    } else {
      operations = ["highlight"];
    }

    const style_meta = options.style_meta || {
      full_row: options.isFullRow || options.fullRow || false,
      alignment: options.alignment || "left",
    };

    step = createStyleStep(
      operations,
      fields,
      cleanedSelectors,
      style_meta,
      `style_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );
  } else {
    let operations = [];
    if (
      options.operations &&
      Array.isArray(options.operations) &&
      options.operations.length > 0
    ) {
      operations = options.operations;
    } else if (operation) {
      operations = [operation];
    } else {
      operations = ["highlight"];
    }

    const style_meta = options.style_meta || {
      full_row: options.isFullRow || options.fullRow || false,
      alignment: options.alignment || "left",
    };

    step = createStyleStep(
      operations,
      fields,
      cleanedSelectors,
      style_meta,
      `style_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    );
  }

  // ============================================================
  // ✅ DEV-ONLY GUARD — 5A
  // INVARIANT: Every step produced here MUST have:
  //              • id          (string, non-empty)
  //              • type        (string, non-empty)
  //              • operation   (string, non-empty)
  //              • selectors   (array of { start, end } strings)
  //              • timestamp   (ISO string)
  //
  // Reason: This is the canonical factory for every transformation
  // step that flows into the recalculation engine and DB. A step
  // that leaves here without any of the required fields will:
  //   – be accepted by the UI (React state doesn't type-check),
  //   – fail silently during recalculation,
  //   – produce a DB row that cannot be re-hydrated on reload.
  //
  // The guards in handleAddTransformationStep (5B) catch mismatches
  // at the consumer side. This guard catches them at the producer
  // side — the earliest possible moment.
  //
  // Owned by: this function.
  // ============================================================
  if (process.env.NODE_ENV !== "production") {
    const missing = [];
    if (!step || typeof step !== "object") {
      console.warn(
        "[INVARIANT:5A] createMinimalTransformationStep produced a non-object step.",
        { step, type, operation },
      );
      return step;
    }
    if (!step.id || typeof step.id !== "string") missing.push("id");
    if (!step.type || typeof step.type !== "string") missing.push("type");
    if (!step.operation || typeof step.operation !== "string")
      missing.push("operation");
    if (!Array.isArray(step.selectors)) missing.push("selectors[]");
    if (!step.timestamp || typeof step.timestamp !== "string")
      missing.push("timestamp");

    if (missing.length > 0) {
      console.warn(
        "[INVARIANT:5A] createMinimalTransformationStep produced an incomplete step.",
        {
          missing,
          stepType: step.type,
          stepOperation: step.operation,
          inputType: type,
          inputOperation: operation,
          step,
        },
      );
    }

    // Type-specific invariants
    if (step.type === "button" && !step.buttonText) {
      console.warn(
        "[INVARIANT:5A] createMinimalTransformationStep produced a button step without buttonText.",
        { step, inputType: type },
      );
    }
    if (step.type === "button-group") {
      if (!Array.isArray(step.buttons) || step.buttons.length === 0) {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced a button-group step without buttons.",
          { step, inputType: type },
        );
      } else if (step.buttons.some((b) => typeof b !== "string")) {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced a button-group step with non-string buttons.",
          { step, buttons: step.buttons },
        );
      }
      if (!step.groupName || typeof step.groupName !== "string") {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced a button-group step without groupName.",
          { step },
        );
      }
    }
    if (step.type === "media") {
      if (
        !step.imageSize ||
        typeof step.imageSize.width !== "number" ||
        typeof step.imageSize.height !== "number"
      ) {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced a media step without valid imageSize.",
          { step, imageSize: step.imageSize },
        );
      }
    }
    if (step.type === "combined") {
      if (
        !step.phases ||
        !step.phases.structural ||
        !step.phases.media ||
        !step.phases.styles
      ) {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced a combined step with incomplete phases.",
          { step, phases: step.phases },
        );
      }
    }

    // Selector shape invariant
    if (Array.isArray(step.selectors)) {
      const badSel = step.selectors.filter(
        (s) =>
          !s ||
          typeof s.start !== "string" ||
          typeof s.end !== "string" ||
          s.start.length === 0 ||
          s.end.length === 0,
      );
      if (badSel.length > 0) {
        console.warn(
          "[INVARIANT:5A] createMinimalTransformationStep produced selectors with non-string start/end.",
          { stepId: step.id, stepType: step.type, badSelectors: badSel },
        );
      }
    }
  }

  console.log("[createMinimalTransformationStep] ✅ Created step:", step);
  return step;
};

// ============================================================
// EXPORT DEFAULT
// ============================================================

export default {
  // Style Transformations
  STYLE_TRANSFORMATIONS,
  STYLE_TRANSFORMATION_LABELS,

  // Structural Transformations
  STRUCTURAL_TRANSFORMATIONS,
  STRUCTURAL_TRANSFORMATION_LABELS,

  // Legacy
  SINGLE_NODE_OPERATIONS,
  MULTI_NODE_OPERATIONS,

  CLASS_MAP,
  SELECTION_TYPES,
  FIELD_DATA_ATTR,

  // Detection
  detectSelectionType,
  generatePositionSelector,

  // Step creation
  createMinimalTransformationStep,

  // Parse utilities
  parseSelectedText,

  // HTML generators
  generateTableHTML,
  generateCardHTML,
  generateListHTML,

  // Phase Wizard
  getTotalOperations,
  addOperationToPhase,
  removeOperationFromPhase,
  finalizeCombinedStep,
  nextPhase,
  prevPhase,

  // Media
  applyMediaTransformation,
};